import classNames from "classnames";
import { useFormik } from "formik";
import { AscButton } from "../../components";
import AscPasswordInput from "../../components/AscInputFields/AscPasswordInput/AscPasswordInput";
import AscHeading from "../../components/AscTypography/AscHeading/AscHeading";
import AscParagraph from "../../components/AscTypography/AscParagraph/AscParagraph";
import AscAuthFlowLayout from "../AscAuthFlowLayout/AscAuthFlowLayout";
import styles from "../AscResetPasswordView/AscResetPasswordView.module.scss";
import * as Yup from "yup";
import YupPassword from "yup-password";

YupPassword(Yup);

export interface AscChangePasswordOnSignUpProps {
  className?: string;
  loading?: boolean;
  onPasswordReset: (passwordResetPayload: {
    password: string;
    confirmPassword: string;
  }) => void;
}

export default function AscChangePasswordOnSignUp({
  className,
  onPasswordReset,
  loading,
}: AscChangePasswordOnSignUpProps) {
  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required("Password is required")
        .minLowercase(1, "Password must contain at least 1 lower case letter")
        .minUppercase(1, "Password must contain at least 1 upper case letter")
        .minNumbers(1, "Password must contain at least 1 number")
        .minSymbols(1, "Password must contain at least 1 special character")
        .min(6, "Password must contain 8 or more characters"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords don't match")
        .required("You must confirm your password"),
    }),
    onSubmit: (values) => {
      onPasswordReset(values);
    },
  });

  return (
    <AscAuthFlowLayout alignment="left">
      <form
        onSubmit={formik?.handleSubmit}
        data-testid="asc-reset-password-view"
        className={classNames(styles.ground, className)}
      >
        <header className={styles.heading}>
          <AscHeading className={styles.title} level={1}>
            Reset password
          </AscHeading>
          <AscParagraph className={styles.subTitle}>
            Please enter your new password
          </AscParagraph>
        </header>

        <div className={styles.form}>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="password">
              Password
            </label>
            <AscPasswordInput
              className={styles.inputField}
              placeholder="1134nnnk123j"
              id="password"
              name="password"
              data-testid="asc-reset-password-password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.password}
            />
            {formik.errors.password && formik.touched.password && (
              <div className={styles.fieldError}>{formik.errors.password}</div>
            )}
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="confirmPassword">
              Confirm Password
            </label>
            <AscPasswordInput
              className={styles.inputField}
              placeholder="1134nnnk123j"
              id="confirmPassword"
              name="confirmPassword"
              data-testid="asc-reset-password-confirm-password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.confirmPassword}
            />
            {formik.errors.confirmPassword &&
              formik.touched.confirmPassword && (
                <div className={styles.fieldError}>
                  {formik.errors.confirmPassword}
                </div>
              )}
          </div>
        </div>

        <footer className={styles.footer}>
          <AscButton
            className={styles.footerButton}
            loading={loading}
            variant="primary"
            size="large"
            fullWidth
            type="submit"
            data-testid="asc-reset-password-submit"
          >
            Reset
          </AscButton>
        </footer>
      </form>
    </AscAuthFlowLayout>
  );
}
